import { createBrowserRouter, redirect, useRouteError } from 'react-router-dom';
import NotFound from './pages/NotFound';
import {
    AdditionalPayResultType,
    createAccountByToken,
    CreateOrderSheetReturnType,
    ExtraChagreType,
    getMallUserIdByToken,
    GroupPurchaseOrderType,
    Inquiries,
    MallBusinessInfoType,
    MallInfo,
    MallNotification,
    MallUserInfoType,
    Mileage,
    OnlineCouponListType,
    OrderDetail,
    OrderDetailV2,
    ProductDetailsType,
    ShippingAddressBookType,
    UserCouponListType,
    UserOrder,
    UserOrderV2,
} from './services/mallUser.service';

import { useState } from 'react';
import { axiosApi } from './helpers/api.helper';
import AccountSettingsPage from './pages/DDM/Account/AccountSettingsPage';
import SocialLoginComponent from './pages/DDM/Auth/SocialLogin';
import { BillingReturnHandler } from './pages/DDM/Billing/BillingReturnHandler';
import DDMHome from './pages/DDM/Home/DDMHome';
import Footer from './pages/DDM/Home/Footer';
import HomeContainer from './pages/DDM/Home/HomeContainer';
import MainNav from './pages/DDM/Home/MainNav';
import MainPage from './pages/DDM/Home/MainPage';
import SubscriptionCheckoutPage from './pages/DDM/Subscription/SubscriptionCheckoutPage';
import SubscriptionPage from './pages/DDM/Subscription/SubscriptionPage';
import SubscriptionSuccessPage from './pages/DDM/Subscription/SubscriptionSuccessPage';
import {
    checkIsMallUser,
    checkRefreshToken,
    checkRefreshTokenBool,
    getTokenByAnonymous,
    isLogined,
    logOut,
    setReferrerRefreshToken,
    setReferrerToken,
} from './services/auth.service';
import { getProductDetail, getProductList, getVendorByPath } from './services/matching.service';
import { PurchaseDrawerStoreState } from './store/purchaseDrawerStore';
import { useSourcingReferrerStore } from './store/sourcingReferrerStore';
import ServicePlan from './pages/DDM/Home/ServicePlan';
import Main2 from './pages/DDM/Home/MainPage2';
import MainPageMobile from './pages/DDM/Home/MainPageMobile';
import isMobile from 'is-mobile';
import { useAnonymousUserStore, useUserStore } from './store/userStore';
import { vendorStore } from './store/vendorStore';
import ProductDetailPage from './pages/DDM/Product/ProductDetailPage';

const HomeContainerWithNav = () => {
    const [currentSection, setCurrentSection] = useState<'ai-fitting' | 'service-plan'>('ai-fitting');

    const handleSectionChange = (index: number) => {
        setCurrentSection(index === 0 ? 'ai-fitting' : 'service-plan');
    };

    return (
        <HomeContainer>
            <MainNav onNavClick={handleSectionChange} />
            <MainPage currentSection={currentSection} />
            <Footer />
        </HomeContainer>
    );
};

const env = import.meta.env.VITE_SET_ENV || 'DEV';
const router = createBrowserRouter([
    {
        path: '/',
        element: isMobile() ? <MainPageMobile /> : <Main2 />,
        // element: <MainPageMobile />,
        // element: <Main2 />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/mobile',
        element: <MainPageMobile />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/:vendorPath',
        element: <DDMHome />,
        id: 'vendorRoute',
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);

            const { vendorPath } = params;
            const vendor = await getVendorInfo(vendorPath || '');
            if (!vendor || vendor.length === 0) {
                // 홈으로 리다이렉트
                return redirect('/');
            }
            const filter = `filter=vendor.id||$eq||${vendor[0]?.id}`;
            const { productList } = await productLoader({ filter });
            return {
                vendor: vendor[0],
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/products/:productId',
        element: <ProductDetailPage />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            await sessionCheckFunc().then((r) => r);

            const { vendorPath, productId } = params;
            // vendor 정보 확인 (세션스토리지에서 먼저 확인)
            const vendor = await getVendorInfo(vendorPath || '');
            if (!vendor || vendor.length === 0) {
                return redirect('/');
            }

            // 제품 정보 조회
            try {
                const product = await getProductDetail({ productId: productId || '' });
                if (!product) {
                    return redirect(`/${vendorPath}`);
                }
                return product;
            } catch (error) {
                console.error('Product fetch error:', error);
                return redirect(`/${vendorPath}`);
            }
        },
    },
    {
        path: '/:vendorPath/all',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await getVendorInfo(vendorPath || '');
            if (!vendor || vendor.length === 0) {
                // 홈으로 리다이렉트
                return redirect('/');
            }
            const filter = `filter=vendor.id||$eq||${vendor[0]?.id}`;
            const { productList } = await productLoader({ filter });
            return {
                vendor: vendor[0],
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/new',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await getVendorInfo(vendorPath || '');
            if (!vendor || vendor.length === 0) {
                // 홈으로 리다이렉트
                return redirect('/');
            }
            const oneMonthAgo = new Date();
            oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
            const formattedDate = oneMonthAgo.toISOString();

            // 벤더 필터와 날짜 필터를 각각 설정
            const filter = `filter=vendor.id||$eq||${vendor[0]?.id}&filter=createdAt||$gte||${formattedDate}`;
            const { productList } = await productLoader({ filter });
            return {
                vendor: vendor[0],
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/best-new',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await getVendorInfo(vendorPath || '');
            if (!vendor || vendor.length === 0) {
                return redirect('/');
            }
            const filter = `filter=vendor.id||$eq||${vendor[0]?.id}&filter=isBestNew||$eq||t`;
            const { productList } = await productLoader({ filter });
            return {
                vendor: vendor[0],
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/today-new',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await getVendorInfo(vendorPath || '');
            if (!vendor || vendor.length === 0) {
                return redirect('/');
            }
            const filter = `filter=vendor.id||$eq||${vendor[0]?.id}&filter=isTodayNew||$eq||t`;
            const { productList } = await productLoader({ filter });
            return {
                vendor: vendor[0],
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/discount',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await getVendorInfo(vendorPath || '');
            if (!vendor || vendor.length === 0) {
                return redirect('/');
            }
            const filter = `filter=vendor.id||$eq||${vendor[0]?.id}&filter=isDiscount||$eq||t`;
            const { productList } = await productLoader({ filter });
            return {
                vendor: vendor[0],
                productList,
            };
        },
    },
    {
        path: '/:vendorPath/sold-out',
        element: <DDMHome />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { vendorPath } = params;
            const vendor = await getVendorInfo(vendorPath || '');
            if (!vendor || vendor.length === 0) {
                return redirect('/');
            }
            const filter = `filter=vendor.id||$eq||${vendor[0]?.id}&filter=isSoldOut||$eq||t`;
            const { productList } = await productLoader({ filter });
            return {
                vendor: vendor[0],
                productList,
            };
        },
    },
    {
        path: '/subscription',
        element: <SubscriptionPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/promo',
        element: <SubscriptionPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/subscription/checkout',
        element: <SubscriptionCheckoutPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/subscription/success',
        element: <SubscriptionSuccessPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/billing/return',
        element: <BillingReturnHandler />,
        errorElement: <ErrorComponent />,
    },
    {
        path: '/account/settings',
        element: <AccountSettingsPage />,
        errorElement: <ErrorComponent />,
    },
    {
        path: 'social/:socialName',
        element: <SocialLoginComponent />,
        errorElement: <ErrorComponent />,
        loader: async ({ params }) => {
            const { socialName: social } = params;
            const queryString = new URLSearchParams(window.location.search);
            const path = String(queryString.get('state')).split('/')[2];
            try {
                if (isLogined()) {
                    return redirect(!!path ? `/mall/${path}` : `/mall`);
                }
                return { social };
            } catch (error) {
                throw error;
            }
        },
    },
    {
        path: '/app-ads.txt',
        element: <p>google.com, pub-2774361938949385, DIRECT, f08c47fec0942fa0</p>,
    },
]);

export interface PersonalnfoLoaderReturnType {
    userInfo: MallUserInfoType;
    shippingList: ShippingAddressBookType[];
}

async function productLoader({ filter }: { filter: string }) {
    try {
        const productList = await getProductList({ page: 1, limit: 20, filter });
        return {
            productList,
        };
    } catch (error) {
        throw error;
    }
}

// vendor 정보를 가져오는 헬퍼 함수 (세션스토리지에 있으면 재사용)
async function getVendorInfo(vendorPath: string) {
    if (!vendorPath) {
        return null;
    }

    // 세션스토리지에서 vendor 정보 확인
    const currentVendor = vendorStore.getState().vendor;
    if (currentVendor) {
        // vendorPath 비교: URL 형식이면 마지막 경로 부분만 추출해서 비교
        const storedVendorPath = currentVendor.vendorPath;
        let storedPath = storedVendorPath;

        // URL 형식인 경우 마지막 경로 부분 추출
        if (storedVendorPath.includes('/')) {
            storedPath = storedVendorPath.split('/').pop() || storedVendorPath;
        }

        // vendorPath도 URL 형식일 수 있으므로 마지막 경로 부분 추출
        let paramPath = vendorPath;
        if (vendorPath.includes('/')) {
            paramPath = vendorPath.split('/').pop() || vendorPath;
        }

        // 경로가 일치하면 재사용
        if (storedPath === paramPath || storedVendorPath === vendorPath || storedVendorPath.endsWith(vendorPath)) {
            // 세션스토리지에 같은 vendorPath의 vendor 정보가 있으면 재사용
            return [currentVendor];
        }
    }

    // 없거나 다른 vendorPath면 API 호출
    const vendor = await getVendorByPath({ vendorPath });
    if (vendor && vendor.length > 0) {
        vendorStore.getState().setVendor(vendor[0]);
    }
    return vendor;
}

export interface MallMainLoader {
    mallInfo: MallInfo['mallInfo'];
    tagNames: string[];
    notificationList: MallNotification['notificationList'];
    mallBusinessInfo: MallBusinessInfoType['mallBusinessInfo'];
    shopName: string;
}
export interface ProductDetailsLoader {
    productDetails: ProductDetailsType;
    drawerProductList: PurchaseDrawerStoreState['drawerProductList'];
    mallInfo: MallInfo['mallInfo'];
    mallBusinessInfo: MallBusinessInfoType['mallBusinessInfo'];
    shopName: string;
}
export interface OrderNPayLoaderReturnType {
    shippingAddressBookList: ShippingAddressBookType[];
    mileageList: Mileage['mileageList'];
    userInfo: MallUserInfoType;
    mallId: string;
    orderInfo: CreateOrderSheetReturnType['orderInfo'];
    couponList: UserCouponListType['items'];
    // salesPolicyInfo: SalesPolicyType,
    state: 'success' | 'fail';
    pgType: string;
    pgBusinessType: string;
    shoppingPoint: number;
    userPoint: number;
    shoppingPointPolicy: {
        id: string;
        policyName: string;
        targetUserType: string;
        pointConversionRate: number;
        maxUsageRate: number;
        minConversionAmount: number;
        minUsageAmount: number;
        isActive: boolean;
        priority: number;
        startDateTime: string;
        endDateTime: string;
    };
}
export interface FooterInfoDataType {
    mallBusinessInfo: MallBusinessInfoType['mallBusinessInfo'];
    shopName: string;
    mallId: string;
    mallInfo: MallInfo['mallInfo'];
}
export interface DestinationListLoaderReturnType {
    shippingAddressBookList: ShippingAddressBookType[];
}
export interface DestinationAdditionLoaderReturnType {
    userInfo: MallUserInfoType;
}
export interface MypageLoaderReturnType {
    userInfo: MallUserInfoType;
    mileage: number;
    mallId: string;
    state: 'success' | 'fail';
}
export interface PersonalnfoLoaderReturnType {
    userInfo: MallUserInfoType;
    shippingList: ShippingAddressBookType[];
}
export interface OrderNShippingInfoReturnType {}
export interface AdditionalPayReturnType {
    extraChargeInfo: ExtraChagreType['extraChargeInfo'];
}
export interface AdditionalPayResultReturnType {
    payResult: AdditionalPayResultType['extraChargeInfo'];
    paramData: {
        accountNumber: string;
        bank: string;
        customerName: string;
        dueDate: string;
        failMessage: string;
    };
}
export interface PayResultReturnType {
    orderDetailInfo: OrderDetailV2['item'];
    paramData: {
        accountNumber: string;
        bank: string;
        customerName: string;
        dueDate: string;
        failMessage: string;
        depositor: string;
    };
    couponList: OnlineCouponListType[];
    pgType: 'TOSS' | 'NICE';
}

/**
 * @description
 * `비회원일 경우 api_key를 이용해 토큰과 ID를 useAnonymousUserStore에 저장합니다.`
 */
export async function fetchNStoreAnonymousTokenNId() {
    const { reset: userReset } = useUserStore.getState();
    /* 비회원일 경우 토큰 발급 */
    const isAnonymousToken = useAnonymousUserStore.getState().accessToken ? true : false;
    // if (!isLogined() && !isAnonymousToken) {
    userReset();
    const { access_token } = await getTokenByAnonymous();
    axiosApi.defaults.headers.common.Authorization = `Bearer ${access_token}`;
    const { mallUserId } = await createAccountByToken(access_token);
    useAnonymousUserStore.setState({
        mallUserId,
        accessToken: access_token,
    });
}

async function sessionCheckFunc(notRefer = true) {
    // 이미 로그인된 경우 세션 체크 스킵
    if (isLogined()) {
        return;
    }

    // URL에 토큰 파라미터가 있는 경우 토큰 로그인 진행 중이므로 비회원 토큰 생성 스킵
    const urlParams = new URLSearchParams(window.location.search);
    const hasTokenParam = urlParams.get('token');
    if (hasTokenParam) {
        return;
    }

    try {
        const isToken = await checkRefreshTokenBool().then((r) => r.data);
        if (isToken) {
            const { reset: anonymousUserReset } = useAnonymousUserStore.getState();
            anonymousUserReset();
            try {
                const { access_token, expires_in } = await checkRefreshToken()
                    .then((r) => r)
                    .catch((err) => {
                        throw err;
                    });
                useUserStore.setState({
                    accessToken: access_token,
                    expiresIn: expires_in,
                });
                axiosApi.defaults.headers.common.Authorization = `Bearer ${access_token}`;
                const isMallUser = await checkIsMallUser(access_token).then((r) => r.data);
                if (isMallUser) {
                    const { mallUserId } = await getMallUserIdByToken(access_token).then((r) => r);
                    useUserStore.setState({ mallUserId });
                } else {
                    const { mallUserId } = await createAccountByToken(access_token).then((r) => r);
                    useUserStore.setState({ mallUserId });
                }
            } catch (err) {
                await logOut();
                window.location.reload();
                throw err;
            }
        } else {
            await fetchNStoreAnonymousTokenNId();
        }
        if (!notRefer) return;
        const queryString = new URLSearchParams(window.location.search);

        // 출처 - 후킹스프 기여도
        const utm_source = queryString.get('utm_source') || '';
        // 매체 - 쇼츠 피드를 보고 넘어옴
        const utm_medium = queryString.get('utm_medium') || '';
        // 인스타셀러 아이디 or 플로잉 닉네임 - 누가 등록한 영상을 보고 넘어왔는가
        // (크롤링 쇼츠의 경우 sns 아이디, 직접 등록한 경우 플로잉 닉네임)
        const utm_campaign = queryString.get('utm_campaign') || '';
        // 상품 아이디 - 해당 상품 구매에만 referrerToken 사용 가능
        const utm_campaign_id = queryString.get('utm_campaign_id') || '';
        const utm_referrer = queryString.get('utm_referrer') || '';
        const utm_content = queryString.get('utm_content') || '';
        const utm_term = queryString.get('utm_term') || '';

        const referer = queryString.get('referer') || '';
        const user_id = queryString.get('user_id') || '';

        const isValidQuery =
            utm_source ||
            utm_medium ||
            utm_campaign ||
            utm_referrer ||
            referer ||
            utm_campaign_id ||
            user_id ||
            utm_content ||
            utm_term;

        const queryPayload = {
            utm_source,
            utm_medium,
            utm_campaign,
            utm_referrer,
            utm_campaign_id,
            user_id,
            referer,
            utm_content,
            utm_term,
        };

        if (isValidQuery) {
            const token: string =
                (await setReferrerToken({ ...queryPayload })
                    .then((r) => r)
                    .catch(() => '')) || '';
            useSourcingReferrerStore.setState({
                referrer_token: token,
            });
        } else {
            const token: string =
                (await setReferrerRefreshToken({ ...queryPayload })
                    .then((r) => r)
                    .catch(() => '')) || '';
            useSourcingReferrerStore.setState({
                referrer_token: token,
            });
        }
    } catch (error) {
        const err: any = error;
        if (!err.status || !err.statusCode) {
            throw { status: -1 };
        }
        try {
            await logOut();
            window.location.reload();
        } finally {
        }
    }
}
export interface OrderListLoaderType {
    orderList: UserOrder[];
    totalCount: number | null;
    isNextLists: boolean;
    isIssue: 'yes' | '';
}
export interface OrderListLoaderV2Type {
    items: UserOrderV2[];
    totalCount: number | null;
    isNextLists: boolean;
    isIssue: boolean;
}

export interface MyInquiryListLoaderType {
    lists: Inquiries['inquiryList'];
    groupPurchaseList: GroupPurchaseOrderType[];
    totalCount: number;
}

export interface NoticePageLoaderType {
    lists: MallNotification['notificationList'];
}

export interface OrderDetailInfoLoaderType {
    orderDetail: OrderDetail['orderDetailInfo'] | 'null';
    isIsland: boolean;
    isJeju: boolean;
    noData?: boolean;
    couponList: OnlineCouponListType[];
}

export interface OrderDetailInfoLoaderTypeV2 {
    orderDetail: OrderDetailV2['item'] | 'null';
    isIsland: boolean;
    isJeju: boolean;
    noData?: boolean;
    couponList: OnlineCouponListType[];
}

interface Designdata {
    spacehome_create_step: number;
    design_type: string;
    bg_filter_type: string;
    bg_type: string;
    description: string;
    sub_description: null;
    bg_color: string;
    text_color: string;
    button_color: string;
    profile_img_url: string;
    banner_img_url: string;
    bg_img_url: string;
    is_button_animated: boolean;
    is_theme: boolean;
    path: string;
}
export interface SpaceHomeV2LoaderReturnType {
    design_data: Designdata;
    item_list: any;
    user_id: string;
    is_mall_opened: boolean;
    nickname: string;
}

function ErrorComponent() {
    const error: any = useRouteError();
    console.log('error::::', error);
    return <NotFound error={error} />;
}

export default router;
